import React, { useState } from 'react';
import { ICONS } from './constants';
import { WorkflowGuidancePanel } from './WorkflowGuidancePanel';

interface ExecutiveReportBuilderProps {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (config: ReportConfig) => void;
}

export interface ReportConfig {
    timeframe: string;
    pillars: {
        intelligence: boolean;
        security: boolean;
        resilience: boolean;
        learning: boolean;
        userStatistics: boolean;
    };
    methodologies: {
        predictiveFootprint: boolean;
        carverMatrix: boolean;
        pestleAnalysis: boolean;
        pmesiiAnalysis: boolean;
    };
}

export const ExecutiveReportBuilder: React.FC<ExecutiveReportBuilderProps> = ({ isOpen, onClose, onGenerate }) => {
    const [config, setConfig] = useState<ReportConfig>({
        timeframe: 'qtd',
        pillars: { intelligence: true, security: true, resilience: true, learning: true, userStatistics: false },
        methodologies: { predictiveFootprint: true, carverMatrix: false, pestleAnalysis: false, pmesiiAnalysis: false }
    });
    const [isGuideOpen, setIsGuideOpen] = useState(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex justify-end animate-fade-in sm:p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm cursor-pointer" onClick={onClose} />
            <div className="relative w-full sm:w-[500px] h-full bg-[#0B1121] border-l border-slate-700/50 sm:rounded-2xl shadow-2xl flex flex-col transform transition-transform animate-slide-in-right overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-blue-900/40 to-black/20">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center">
                            <span className="text-blue-500 mr-2">{ICONS.document}</span>
                            Executive Report Builder
                        </h2>
                        <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest font-mono">Operations Command Module</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => setIsGuideOpen(true)} className="text-blue-400 hover:text-white px-3 py-2 rounded-lg hover:bg-blue-500/20 transition-colors bg-blue-500/10 border border-blue-500/30 text-xs font-bold uppercase tracking-widest flex items-center shadow-inner">
                            💡 Help
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                    
                    {/* Timeframe Selection */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-widest mb-4">Reporting Period</h3>
                        <div className="grid grid-cols-2 gap-3">
                            {['mtd', 'qtd', 'ytd', '12m'].map((tf) => (
                                <button
                                    key={tf}
                                    onClick={() => setConfig({ ...config, timeframe: tf })}
                                    className={`py-3 px-4 rounded-lg font-bold text-sm border transition-all ${
                                        config.timeframe === tf 
                                            ? 'bg-blue-600 border-blue-500 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]' 
                                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
                                    }`}
                                >
                                    {tf === 'mtd' ? 'Month to Date' : tf === 'qtd' ? 'Quarter to Date' : tf === 'ytd' ? 'Year to Date' : 'Trailing 12M'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Pillar Inclusions */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-widest mb-4">Pillar Aggregation</h3>
                        <div className="space-y-3">
                            <label className="flex items-center p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                                <input type="checkbox" checked={config.pillars.intelligence} onChange={(e) => setConfig({ ...config, pillars: { ...config.pillars, intelligence: e.target.checked } })} className="form-checkbox h-5 w-5 text-blue-600 rounded border-slate-600 bg-slate-700 focus:ring-blue-500" />
                                <span className="ml-3 text-sm font-medium text-slate-200">Geopolitical Intelligence</span>
                            </label>
                            <label className="flex items-center p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                                <input type="checkbox" checked={config.pillars.security} onChange={(e) => setConfig({ ...config, pillars: { ...config.pillars, security: e.target.checked } })} className="form-checkbox h-5 w-5 text-emerald-500 rounded border-slate-600 bg-slate-700 focus:ring-emerald-500" />
                                <span className="ml-3 text-sm font-medium text-slate-200">Security Risk Management (SRM)</span>
                            </label>
                            <label className="flex items-center p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                                <input type="checkbox" checked={config.pillars.resilience} onChange={(e) => setConfig({ ...config, pillars: { ...config.pillars, resilience: e.target.checked } })} className="form-checkbox h-5 w-5 text-indigo-500 rounded border-slate-600 bg-slate-700 focus:ring-indigo-500" />
                                <span className="ml-3 text-sm font-medium text-slate-200">Business Resilience & Crisis Mgt</span>
                            </label>
                            <label className="flex items-center p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                                <input type="checkbox" checked={config.pillars.learning} onChange={(e) => setConfig({ ...config, pillars: { ...config.pillars, learning: e.target.checked } })} className="form-checkbox h-5 w-5 text-cyan-500 rounded border-slate-600 bg-slate-700 focus:ring-cyan-500" />
                                <span className="ml-3 text-sm font-medium text-slate-200">Learning Management (LMS)</span>
                            </label>
                            <label className="flex items-center p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-900/30 transition-colors">
                                <input type="checkbox" checked={config.pillars.userStatistics} onChange={(e) => setConfig({ ...config, pillars: { ...config.pillars, userStatistics: e.target.checked } })} className="form-checkbox h-5 w-5 text-blue-400 rounded border-slate-600 bg-slate-700 focus:ring-blue-400" />
                                <span className="ml-3 text-sm font-bold text-blue-400">App User Statistics & Engagement</span>
                            </label>
                        </div>
                    </div>

                    {/* IC Methodologies */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-widest mb-4">Analytic Methodologies</h3>
                        <div className="space-y-3">
                            <label className="flex items-start p-4 glass-panel cursor-pointer hover:border-blue-500/50 transition-colors">
                                <input type="checkbox" checked={config.methodologies.predictiveFootprint} onChange={(e) => setConfig({ ...config, methodologies: { ...config.methodologies, predictiveFootprint: e.target.checked } })} className="form-checkbox mt-1 h-5 w-5 text-blue-600 rounded border-slate-600 bg-slate-700 focus:ring-blue-500" />
                                <div className="ml-3">
                                    <span className="block text-sm font-bold text-blue-400">Predictive Footprint Analysis</span>
                                    <span className="block text-xs text-slate-400 mt-1">Cross-references global assets against future threat heatmaps to recommend spatial restructuring.</span>
                                </div>
                            </label>
                            <label className="flex items-start p-4 glass-panel cursor-pointer hover:border-blue-500/50 transition-colors">
                                <input type="checkbox" checked={config.methodologies.carverMatrix} onChange={(e) => setConfig({ ...config, methodologies: { ...config.methodologies, carverMatrix: e.target.checked } })} className="form-checkbox mt-1 h-5 w-5 text-blue-600 rounded border-slate-600 bg-slate-700 focus:ring-blue-500" />
                                <div className="ml-3">
                                    <span className="block text-sm font-bold text-slate-200">CARVER Vulnerability Matrix</span>
                                    <span className="block text-xs text-slate-400 mt-1">Include Criticality, Accessibility, Recuperability, Vulnerability, Effect, and Recognizability scores for top assets.</span>
                                </div>
                            </label>
                            <label className="flex items-start p-4 glass-panel cursor-pointer hover:border-blue-500/50 transition-colors">
                                <input type="checkbox" checked={config.methodologies.pestleAnalysis} onChange={(e) => setConfig({ ...config, methodologies: { ...config.methodologies, pestleAnalysis: e.target.checked } })} className="form-checkbox mt-1 h-5 w-5 text-blue-600 rounded border-slate-600 bg-slate-700 focus:ring-blue-500" />
                                <div className="ml-3">
                                    <span className="block text-sm font-bold text-slate-200">PESTLE Strategic Factors</span>
                                    <span className="block text-xs text-slate-400 mt-1">Macro-environmental overlay indexing political, economic, socio-cultural, and technological forces.</span>
                                </div>
                            </label>
                            <label className="flex items-start p-4 glass-panel cursor-pointer hover:border-blue-500/50 transition-colors">
                                <input type="checkbox" checked={config.methodologies.pmesiiAnalysis} onChange={(e) => setConfig({ ...config, methodologies: { ...config.methodologies, pmesiiAnalysis: e.target.checked } })} className="form-checkbox mt-1 h-5 w-5 text-blue-600 rounded border-slate-600 bg-slate-700 focus:ring-blue-500" />
                                <div className="ml-3">
                                    <span className="block text-sm font-bold text-slate-200">PMESII Systemic Analysis</span>
                                    <span className="block text-xs text-slate-400 mt-1">Cross-domain evaluation of Political, Military, Economic, Social, Information, and Infrastructure systems.</span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-white/10 bg-black/40">
                    <button 
                        onClick={() => onGenerate(config)}
                        className="w-full flex justify-center items-center py-4 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg font-black uppercase tracking-widest text-sm shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all transform hover:-translate-y-0.5"
                    >
                        {ICONS.document}
                        <span className="ml-2">Generate Executive Report</span>
                    </button>
                    <p className="text-center font-mono text-[10px] text-slate-500 mt-4 uppercase tracking-widest cursor-default">Report payload classified // SECURE</p>
                </div>
            </div>

            <WorkflowGuidancePanel 
                isOpen={isGuideOpen} 
                onClose={() => setIsGuideOpen(false)} 
                moduleKey="reportBuilder" 
            />
        </div>
    );
};
